import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { apiFetch } from './api';
import { useAuthStore } from './auth-store';
import { useNotificationsStore, type TicketEvent } from './notifications-store';

/**
 * Hook que mantiene una conexión SSE a `/api/v1/events/stream` y dispatch
 * cada evento al store. Se llama una sola vez desde Layout mientras el
 * usuario está autenticado.
 *
 * Flujo:
 *   1. POST /events/sse-token con Bearer → recibe token de 60s.
 *   2. new EventSource(`/api/v1/events/stream?token=<token>`).
 *   3. onmessage → useNotificationsStore.addEvent + toast + sonido + browser notif.
 *   4. onerror → cerrar y reintentar después de un backoff.
 *
 * Pausa cuando la pestaña está oculta más de 5 min (cierra EventSource).
 * Reabre al volver el foco.
 */

const RECONNECT_BASE_MS = 2000;
const RECONNECT_MAX_MS = 30_000;
const IDLE_PAUSE_MS = 5 * 60 * 1000;

interface SseTokenResponse {
  token: string;
  expires_in: number;
}

export function useTicketEventsStream() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const addEvent = useNotificationsStore((s) => s.addEvent);
  const setConnected = useNotificationsStore((s) => s.setConnected);
  const soundEnabled = useNotificationsStore((s) => s.soundEnabled);
  const browserNotifEnabled = useNotificationsStore((s) => s.browserNotifEnabled);

  // refs para que las callbacks accedan al valor actual sin re-attach.
  const soundEnabledRef = useRef(soundEnabled);
  const browserNotifEnabledRef = useRef(browserNotifEnabled);
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);
  useEffect(() => { browserNotifEnabledRef.current = browserNotifEnabled; }, [browserNotifEnabled]);

  useEffect(() => {
    if (!accessToken) return;

    let es: EventSource | null = null;
    let reconnectTimer: number | undefined;
    let reconnectDelay = RECONNECT_BASE_MS;
    let cancelled = false;
    let pausedAt: number | null = null;

    const closeStream = () => {
      if (es) {
        es.close();
        es = null;
      }
      setConnected(false);
    };

    const handleMessage = (evt: MessageEvent) => {
      try {
        const data = JSON.parse(evt.data) as TicketEvent;
        addEvent(data);
        dispatchUx(data, {
          soundEnabled: soundEnabledRef.current,
          browserNotifEnabled: browserNotifEnabledRef.current,
        });
      } catch {
        // Mensaje malformado — ignorar.
      }
    };

    const open = async () => {
      if (cancelled) return;
      try {
        const { token } = await apiFetch<SseTokenResponse>('/events/sse-token', {
          method: 'POST',
        });
        if (cancelled) return;

        es = new EventSource(`/api/v1/events/stream?token=${encodeURIComponent(token)}`);

        es.addEventListener('ready', () => {
          setConnected(true);
          reconnectDelay = RECONNECT_BASE_MS;
        });
        es.addEventListener('message', handleMessage);

        es.onerror = () => {
          // EventSource intenta reconectar solo, pero el token ya está
          // consumido (one-shot). Cerramos y pedimos token nuevo.
          closeStream();
          if (cancelled) return;
          reconnectTimer = window.setTimeout(open, reconnectDelay);
          reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS);
        };
      } catch {
        // Falla pidiendo el token (red, 429, etc.) — backoff.
        if (cancelled) return;
        reconnectTimer = window.setTimeout(open, reconnectDelay);
        reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS);
      }
    };

    const checkIdle = () => {
      if (document.visibilityState === 'hidden') {
        if (pausedAt === null) pausedAt = Date.now();
        else if (Date.now() - pausedAt > IDLE_PAUSE_MS && es) {
          closeStream();
        }
      } else {
        pausedAt = null;
        if (!es) void open();
      }
    };

    void open();
    const idleInterval = window.setInterval(checkIdle, 30_000);
    document.addEventListener('visibilitychange', checkIdle);

    return () => {
      cancelled = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      window.clearInterval(idleInterval);
      document.removeEventListener('visibilitychange', checkIdle);
      closeStream();
    };
  }, [accessToken, addEvent, setConnected]);
}

interface UxFlags {
  soundEnabled: boolean;
  browserNotifEnabled: boolean;
}

function dispatchUx(event: TicketEvent, flags: UxFlags): void {
  const code = event.ticket_code ?? `#${event.ticket_id}`;
  const subject = event.ticket_subject ?? '';

  // Toast — solo para eventos relevantes para el agente conectado.
  // El evento 'created' es el más importante.
  if (event.type === 'created') {
    toast(`Ticket nuevo: ${code}`, {
      description: subject || 'Sin asunto',
      action: {
        label: 'Abrir',
        onClick: () => {
          window.location.assign(`/admin/tickets/${event.ticket_code ?? event.ticket_id}`);
        },
      },
      duration: 7000,
    });
  } else if (event.type === 'message_added') {
    toast(`Mensaje nuevo en ${code}`, { description: subject, duration: 4000 });
  } else if (event.type === 'status_changed') {
    const p = event.payload as { from?: string; to?: string };
    if (p?.to) {
      toast(`${code}: ${p.from ?? '?'} → ${p.to}`, { duration: 3000 });
    }
  } else if (event.type === 'assigned') {
    toast(`${code} asignado`, { duration: 3000 });
  }

  // Sonido.
  if (flags.soundEnabled && event.type === 'created') {
    void playChime();
  }

  // Notificación nativa del browser (solo si la pestaña NO está visible).
  if (
    flags.browserNotifEnabled &&
    typeof Notification !== 'undefined' &&
    Notification.permission === 'granted' &&
    document.visibilityState !== 'visible' &&
    event.type === 'created'
  ) {
    try {
      const notif = new Notification('Soporte Vipsoft', {
        body: `Ticket ${code}: ${subject}`,
        icon: '/favicon.svg',
        tag: `ticket-${event.ticket_id}`,
      });
      notif.onclick = () => {
        window.focus();
        window.location.assign(`/admin/tickets/${event.ticket_code ?? event.ticket_id}`);
      };
    } catch {
      // Algunos browsers tiran si el permission se revoca.
    }
  }
}

async function playChime(): Promise<void> {
  try {
    const audio = new Audio('/sounds/notify.wav');
    audio.volume = 0.5;
    await audio.play();
  } catch {
    // Autoplay bloqueado o archivo no disponible. Silencioso.
  }
}
